import { useEffect, useMemo, useRef, useState } from 'react'

type Setter<T> = React.Dispatch<React.SetStateAction<T | undefined>>

type Options<T> = Partial<{
  serializer: (object: T | undefined) => string
  parser: (val: string) => T | undefined
  logger: (error: unknown) => void
  syncData: boolean
}>

export default function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: Options<T>,
): [T, Setter<T>] {
  const opts = useMemo(() => ({
    serializer: JSON.stringify,
    parser: JSON.parse,
    logger: console.log,
    syncData: true,
    ...options,
  }), [options])

  const { serializer, parser, logger, syncData } = opts
  const rawValueRef = useRef<string | null>(null)

  const [value, setValue] = useState<T | undefined>(() => {
    if (typeof window === 'undefined') {
      return defaultValue
    }
    try {
      rawValueRef.current = window.localStorage.getItem(key)
      return rawValueRef.current ? parser(rawValueRef.current) : defaultValue
    } catch (e) {
      logger(e)
      return defaultValue
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      if (value !== undefined) {
        const newValue = serializer(value)
        const oldValue = rawValueRef.current
        rawValueRef.current = newValue
        window.localStorage.setItem(key, newValue)
        window.dispatchEvent(new StorageEvent('storage', {
          storageArea: window.localStorage,
          url: window.location.href,
          key,
          newValue,
          oldValue,
        }))
      } else {
        window.localStorage.removeItem(key)
        window.dispatchEvent(new StorageEvent('storage', {
          storageArea: window.localStorage,
          url: window.location.href,
          key,
        }))
      }
    } catch (e) {
      logger(e)
    }
  }, [value])

  useEffect(() => {
    if (!syncData || typeof window === 'undefined') {
      return
    }

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key !== key || e.storageArea !== window.localStorage) {
        return
      }
      try {
        if (e.newValue !== rawValueRef.current) {
          rawValueRef.current = e.newValue
          setValue(e.newValue ? parser(e.newValue) : undefined)
        }
      } catch (e) {
        logger(e)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key, syncData])

  return [value as T, setValue]
}
